// packages/components/src/support-ai/ConversationListPanel.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@dopomogai/ui';
import clsx from 'clsx';

// A minimal interface this component expects. The actual `Customer` type in the app will conform to this.
interface Conversation {
  id: string;
  name: string;
  company: string;
  avatarUrl?: string;
  status: 'Online' | 'Offline' | 'Needs Intervention';
  lastMessage: {
    snippet: string;
    timestamp: string;
  };
  tags: string[];
}

interface ConversationListPanelProps<T extends Conversation> {
  conversations: T[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
}

const statusColorMap = {
  'Online': 'bg-green-500',
  'Offline': 'bg-slate-400',
  'Needs Intervention': 'bg-red-500',
};

export const ConversationListPanel = <T extends Conversation>({
  conversations,
  activeConversationId,
}: ConversationListPanelProps<T>) => {
  return (
    <div className="h-full flex flex-col">
      <header className="p-4 border-b-2 border-black bg-white flex-shrink-0">
        <h2 className="text-xl font-bold text-slate-900">Active Conversations</h2>
      </header>
      <div className="flex-grow overflow-y-auto">
        {conversations.map((convo) => {
          const isActive = convo.id === activeConversationId;
          return (
            <Link to={`/support-ai/customer/${convo.id}`} key={convo.id} className="block">
              <div
                className={clsx(
                  'p-4 border-b border-slate-200 cursor-pointer transition-colors duration-150',
                  isActive ? 'bg-emerald-50' : 'hover:bg-slate-50'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={convo.avatarUrl || '/avatars/default-avatar.png'}
                      alt={convo.name}
                      className="h-10 w-10 rounded-full border-2 border-black object-cover"
                    />
                    <span
                      className={clsx(
                        'absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white',
                        statusColorMap[convo.status]
                      )}
                    />
                  </div>
                  <div className="flex-grow overflow-hidden">
                    <div className="flex justify-between items-baseline">
                        <h3 className="font-bold text-slate-800 truncate">{convo.name}</h3>
                        <p className="text-xs text-slate-400 flex-shrink-0">{new Date(convo.lastMessage.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit'})}</p>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{convo.lastMessage.snippet}</p>
                     <div className="flex flex-wrap gap-1 mt-2">
                        {convo.tags.map(tag => <Badge key={tag} text={tag} accentColor='gray' />)}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};